import { Track } from "@/src/lib/tracking";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { deviceDetector } from "@ospweb/sdk-ui";
import {
  PayChannel,
  PayType,
  SubscriptionCapability,
  type PaymentPlan,
} from "../features/api/memberApi";
import { QUERY_KEYS } from "@config/constant";
import { useGetListPayment } from "./useGetListPayment";

interface SubscriptionListResult {
  enabled: boolean;
}

const formatPlanDate = (time?: number): string =>
  time ? dayjs(time).format("MMM DD, YYYY") : "—";

/** 旧判定：按渠道 + 当前设备决定能否走门户 / autopay / 应用商店管理 */
const canCancelByChannel = (payChannel?: string): boolean =>
  payChannel === PayChannel.STRIPE ||
  payChannel === PayChannel.PAYPAL ||
  (payChannel === PayChannel.GOOGLE_IAP && deviceDetector.isAndroidApp()) ||
  (payChannel === PayChannel.APPLE_IAP && deviceDetector.isIOSApp());

/**
 * 订阅行是否可管理（EDG-552）。能力非空时以能力为准（C-5：后端加渠道前端不改）；
 * 能力**缺失或为空集**都回退按渠道的旧判定 —— 后端对 PAYPAL / IAP 下发的就是空集（be-design §能力声明），
 * 只判 `capabilities` 是否存在会把这些存量订阅全部判成不可管理（PC 按钮置灰、H5 菜单项消失）。
 */
export function resolveCanCancel(
  plan: Pick<PaymentPlan, "capabilities" | "pay_channel">,
): boolean {
  const { capabilities } = plan;
  if (!capabilities?.length) {
    return canCancelByChannel(plan.pay_channel);
  }
  return (
    capabilities.includes(SubscriptionCapability.CANCEL_AT_PERIOD_END) ||
    capabilities.includes(SubscriptionCapability.HOSTED_PORTAL)
  );
}

export const useSubscriptionList = ({ enabled }: SubscriptionListResult) => {
  const { getListPayment } = useGetListPayment();
  return useQuery({
    queryKey: [QUERY_KEYS.PAYMENT_LIST_PAYMENT, "subscriptionList"],
    queryFn: () => getListPayment({ pay_type: PayType.CONTINUOUS }),
    enabled,
    select: (list: PaymentPlan[]) => {
      Track.log("info", "member", "-[fetchSubscriptionStatus]", {
        cause: String(list),
      });
      const items = list
        .filter(
          (item) =>
            ["SUSPENDED", "ACTIVE"].includes(item.status ?? "") &&
            item.pay_channel,
        )
        .map((item) => {
          const nextPaymentDate = formatPlanDate(item.next_billing_time);
          const capabilities = item.capabilities;
          const canCancel = resolveCanCancel(item);
          return {
            pay_channel: item.pay_channel!,
            product_level: item.product_level,
            nextPaymentDate,
            canCancel,
            status: item.status!,
            id: item.id!,
            links:
              item.links?.find((link) => link.rel === "customer_session_url")
                ?.href ?? "",
            capabilities,
            autoRenew: item.auto_renew,
            paymentIssue: item.payment_issue,
            currentPeriodEndDate: formatPlanDate(item.current_period_end),
          };
        });
      return items;
    },
  });
};
